import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { toastError } from '@strivelabs/ui-core';
import { formatBytes } from '@strivelabs/ui-blocks';
import {
  listLibraryFiles,
  uploadFile,
  type LibraryFileType,
} from '@/services/DataService';
import {
  CHAT_MAX_FILE_SIZE_BYTES,
  LIBRARY_ALLOWED_FILE_MIME_TYPES,
} from '@/constants/fileUpload';
import {
  LIBRARY_FILES_QUERY_KEY,
  LIBRARY_PAGE_SIZE,
  LIBRARY_PREVIEW_READ_TIMEOUT_MS,
  LIBRARY_UPLOAD_CONCURRENCY,
  UPLOADED_FILE_LIST_QUERY_KEYS,
} from '@/constants/library';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import { isImageType } from '@/components/library/libraryEntry';

/** Whose files the library shows: everyone's in the workspace, or the caller's. */
export type LibraryScope = 'all' | 'mine';

interface LibraryFilesFilters {
  scope: LibraryScope;
  type: LibraryFileType | null;
  /** Already settled (debounced and trimmed) by the caller. */
  search: string;
}

/**
 * The workspace's uploads, newest first, one page at a time.
 *
 * While a new filter's first page loads, the previous list stays on screen
 * (`isPlaceholderData`) rather than dropping to a skeleton, so narrowing the
 * library never flashes it empty.
 */
export const useLibraryFiles = ({ scope, type, search }: LibraryFilesFilters) => {
  return useInfiniteQuery({
    queryKey: [ ...LIBRARY_FILES_QUERY_KEY, { scope, type, search } ],
    queryFn: ({ pageParam }) => listLibraryFiles({
      cursor: pageParam,
      limit: LIBRARY_PAGE_SIZE,
      type,
      mine: scope === 'mine',
      search,
    }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    // Fresh on every visit, unlike the app's five-minute default: teammates
    // upload too, and the library is where a reader goes to find what is new.
    staleTime: 0,
  });
};

/** A file on its way up, shown in the library until the list has it. */
export interface PendingUpload {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  /** A local preview of an image; null for anything else. */
  previewUrl: string | null;
  /** The image's own size, read locally, so its tile takes its final shape. */
  width: number | null;
  height: number | null;
}

/**
 * An image's natural size, read in the browser before it is uploaded. Gives
 * up after a moment rather than holding the upload back: an image too slow to
 * decode here just gets a square tile until the list has its stored size.
 *
 * @param url - An object URL for the image.
 *
 * @returns Its width and height, or null when it cannot be decoded in time.
 */
const readImageSize = (url: string): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const image = new Image();
    const giveUp = setTimeout(() => resolve(null), LIBRARY_PREVIEW_READ_TIMEOUT_MS);
    image.onload = () => {
      clearTimeout(giveUp);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      clearTimeout(giveUp);
      resolve(null);
    };
    image.src = url;
  });
};

/**
 * Why a picked file cannot be uploaded, or null when it can. Mirrors the
 * server's limits so a file it would refuse never makes the round trip.
 *
 * @param file - The picked file.
 *
 * @returns The reason, for a toast.
 */
const uploadRejection = (file: File): string | null => {
  if (!(LIBRARY_ALLOWED_FILE_MIME_TYPES as readonly string[]).includes(file.type)) {
    return 'Only PDF, PNG, JPG, GIF and WebP files can be uploaded.';
  }
  if (file.size === 0) {
    return 'The file is empty.';
  }
  if (file.size > CHAT_MAX_FILE_SIZE_BYTES) {
    return `Files must be under ${formatBytes(CHAT_MAX_FILE_SIZE_BYTES)}.`;
  }
  return null;
};

/**
 * Run `task` over `items`, at most `limit` at a time.
 *
 * @param items - What to run over.
 * @param limit - How many may run at once.
 * @param task - The work for one item.
 */
const runPooled = async <T>(items: T[], limit: number, task: (item: T) => Promise<void>) => {
  const queue = [ ...items ];
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      await task(item);
    }
  });
  await Promise.all(workers);
};

let pendingSequence = 0;

/**
 * Upload files into the library.
 *
 * Each accepted file shows straight away as a pending entry (with a local
 * preview for an image), and leaves only once the refreshed list holds the
 * real one, so nothing blinks out between the upload finishing and the list
 * catching up. A file the server would refuse is turned away here with a
 * toast; a failed upload says so with its own.
 */
export const useLibraryUpload = () => {
  const queryClient = useQueryClient();
  const trackEvent = useTrack();
  const [ pending, setPending ] = useState<PendingUpload[]>([]);
  const previewUrls = useRef(new Set<string>());

  // A preview outliving the page would hold the image in memory for nothing.
  useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const settle = useCallback((entry: PendingUpload) => {
    setPending((held) => held.filter((candidate) => candidate.id !== entry.id));
    if (entry.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl);
      previewUrls.current.delete(entry.previewUrl);
    }
  }, []);

  const upload = useCallback(async (files: File[]) => {
    const rejected: { file: File; reason: string }[] = [];
    const accepted: File[] = [];
    files.forEach((file) => {
      const reason = uploadRejection(file);
      if (reason) {
        rejected.push({ file, reason });
      } else {
        accepted.push(file);
      }
    });

    if (rejected.length === 1) {
      toastError(`Couldn't upload ${rejected[0].file.name}`, rejected[0].reason);
    } else if (rejected.length > 1) {
      toastError(`Couldn't upload ${rejected.length} files`, rejected[0].reason);
    }

    if (accepted.length === 0) {
      return;
    }

    const entries = await Promise.all(accepted.map(async (file) => {
      pendingSequence += 1;
      const previewUrl = isImageType(file.type) ? URL.createObjectURL(file) : null;
      if (previewUrl) {
        previewUrls.current.add(previewUrl);
      }
      const size = previewUrl ? await readImageSize(previewUrl) : null;
      const entry: PendingUpload = {
        id: `pending-${pendingSequence}`,
        filename: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        previewUrl,
        width: size?.width ?? null,
        height: size?.height ?? null,
      };
      return { file, entry };
    }));

    // Newest first, as the list itself is.
    setPending((held) => [ ...entries.map(({ entry }) => entry).reverse(), ...held ]);

    await runPooled(entries, LIBRARY_UPLOAD_CONCURRENCY, async ({ file, entry }) => {
      try {
        await uploadFile(file);
        trackEvent(ANALYTICS_EVENTS.LIBRARY_FILE_UPLOADED, {
          file_type: file.type || 'unknown',
          file_size_bytes: file.size,
        });
        await Promise.all(UPLOADED_FILE_LIST_QUERY_KEYS.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey })));
      } catch (error) {
        toastError(
          `Couldn't upload ${file.name}`,
          error instanceof Error && error.message ? error.message : 'Please try again.',
        );
      } finally {
        settle(entry);
      }
    });
  }, [ queryClient, settle, trackEvent ]);

  return { pending, upload };
};
